export const ROUTES = {
  // Public
  landing: () => '/',
  news: () => '/news',
  newsArticle: (slug: string) => `/news/${slug}`,
  ads: () => '/ads',
  links: () => '/links',
  /** راهنمای معامله، قوانین، درباره ما، تماس: the operator edited pages. */
  contentPage: (slug: string) => `/pages/${slug}`,
  market: () => '/market',
  marketPrices: () => '/market/prices',
  marketLme: () => '/market/lme',
  marketFx: () => '/market/fx',
  marketDashboard: () => '/market/dashboard',
  marketPriceIndex: () => '/market/price-index',
  marketHistory: () => '/market/history',
  marketArchive: () => '/market/archive',

  // Entry and onboarding
  login: (next?: string) => (next ? `/login?next=${encodeURIComponent(next)}` : '/login'),
  register: () => '/register',
  registerStep: (step: OnboardingStepSlug) => `/register/${step}`,

  // Member
  dashboard: () => '/dashboard',
  board: () => '/board',
  boardFiltered: (q: Record<string, string>) => withQuery('/board', q),
  rfqBoard: () => '/board/rfq',
  dealsBoard: () => '/board/deals',

  offerNew: () => '/offers/new',
  offerNewStep: (step: OfferWizardStep) => `/offers/new/${step}`,
  offerNewBrand: () => '/offers/new/product/brand',
  offerDetail: (offerId: number | string) => `/offers/${offerId}`,
  offerEdit: (offerId: number | string) => `/offers/${offerId}/edit`,
  offerCancel: (offerId: number | string) => `/offers/${offerId}/cancel`,
  offerBid: (offerId: number | string) => `/offers/${offerId}/bid`,
  myOffers: () => '/offers/mine',

  rfqNew: () => '/rfqs/new',
  rfqNewStep: (step: RfqWizardStep | RfqWizardLegacyStep) => `/rfqs/new/${step}`,
  rfqDetail: (rfqId: number | string) => `/rfqs/${rfqId}`,
  rfqBid: (rfqId: number | string) => `/rfqs/${rfqId}/bid`,
  rfqEdit: (rfqId: number | string) => `/rfqs/${rfqId}/edit`,
  rfqCancel: (rfqId: number | string) => `/rfqs/${rfqId}/cancel`,
  myRfqs: () => '/rfqs/mine',

  bids: () => '/bids',
  bidHistory: (bidId: number | string) => `/bids/${bidId}/history`,

  negotiation: (negotiationId: number | string) => `/negotiations/${negotiationId}`,
  deals: () => '/deals',
  dealRoom: (dealId: number | string) => `/deals/${dealId}`,
  dealNotice: (dealId: number | string, side?: 'buyer' | 'seller') =>
    side ? `/deals/${dealId}/notice?side=${side}` : `/deals/${dealId}/notice`,
  dealDocuments: (dealId: number | string) => `/deals/${dealId}/documents`,
  dealDelivery: (dealId: number | string) => `/deals/${dealId}/delivery`,

  documents: () => '/documents',
  notifications: () => '/notifications',
  favorites: () => '/favorites',
  requests: () => '/requests',
  wallet: () => '/wallet',
  walletTransactions: () => '/wallet/transactions',
  grade: () => '/account/grade',
  claims: () => '/claims',
  claimNew: (dealId?: number | string) =>
    dealId === undefined ? '/claims/new' : `/claims/new?deal=${dealId}`,
  claim: (claimNo: number | string) => `/claims/${claimNo}`,

  settings: () => '/settings',
  settingsProfile: () => '/settings/profile',
  settingsUsers: () => '/settings/users',
  settingsKyc: () => '/settings/kyc',
  settingsNotifications: () => '/settings/notifications',
  settingsSecurity: () => '/settings/security',

  // Operator console.
  admin: () => '/admin',
  adminLogin: (next?: string) =>
    next ? `/admin/login?next=${encodeURIComponent(next)}` : '/admin/login',
  adminForbidden: () => '/admin/403',
  adminMatching: (offerId?: number | string) =>
    offerId ? `/admin/matching?offer=${offerId}` : '/admin/matching',
  adminOffers: (offerId?: number | string) =>
    offerId ? `/admin/offers?offer=${offerId}` : '/admin/offers',
  adminRfqs: (rfqId?: number | string) => (rfqId ? `/admin/rfqs?rfq=${rfqId}` : '/admin/rfqs'),
  adminKyc: (fileId?: number | string) => (fileId ? `/admin/kyc?file=${fileId}` : '/admin/kyc'),
  adminBrands: (brandId?: number | string) =>
    brandId ? `/admin/brands?brand=${brandId}` : '/admin/brands',
  adminClaims: (claimId?: number | string) =>
    claimId ? `/admin/claims?claim=${claimId}` : '/admin/claims',
  adminUsers: (userId?: number | string) =>
    userId ? `/admin/users?user=${userId}` : '/admin/users',
  adminRoles: () => '/admin/roles',
  adminDeals: (dealId?: number | string) =>
    dealId ? `/admin/deals?deal=${dealId}` : '/admin/deals',
  adminPrices: () => '/admin/prices',
  adminNews: () => '/admin/news',
  adminContent: () => '/admin/content',
  adminReports: () => '/admin/reports',
  adminAudit: () => '/admin/audit',
  adminConfig: () => '/admin/config',

  // Errors
  forbidden: () => '/403',
  notFound: () => '/404',
} as const

/** The complete-profile flow, the screens a member walks to unlock trading. */
export type OnboardingStepSlug =
  | 'account'
  | 'type'
  | 'profile'
  | 'contract'
  | 'done'
  | 'pending'
  | 'rejected'

/** What `GET /onboarding/me` answers with. `active` is the dashboard, not a screen. */
export type OnboardingStage = 'type' | 'profile' | 'contract' | 'pending' | 'rejected' | 'active'

export type OfferWizardStep =
  | 'deal'
  | 'spec'
  | 'weight'
  | 'payment'
  | 'delivery'
  | 'review'
  | 'submitted'
/** The slugs of the earlier layout, still found in saved links and notifications. */
export type LegacyOfferWizardStep = 'product' | 'terms'
/** «ثبت تقاضای خرید», the sale form's six steps under the same names, then the receipt. */
export type RfqWizardStep =
  | 'deal'
  | 'spec'
  | 'weight'
  | 'payment'
  | 'delivery'
  | 'review'
  | 'submitted'

/** Slugs of the old four step demand wizard; a link to one opens the step that holds its fields. */
export type RfqWizardLegacyStep = 'product' | 'terms'

export const OFFER_WIZARD_STEPS: Array<{ slug: OfferWizardStep; label: string }> = [
  { slug: 'deal', label: 'مشخصات معامله' },
  { slug: 'spec', label: 'مشخصات فنی کالا' },
  { slug: 'weight', label: 'شرایط وزنی' },
  { slug: 'payment', label: 'قیمت و پرداخت' },
  { slug: 'delivery', label: 'تحویل و اعتبار' },
  { slug: 'review', label: 'بازبینی نهایی' },
  { slug: 'submitted', label: 'ارسال شد' },
]

/** Where a link written for the earlier layout lands: */
export const OFFER_WIZARD_LEGACY_STEPS: Record<LegacyOfferWizardStep, OfferWizardStep> = {
  product: 'deal',
  terms: 'deal',
}

export const RFQ_WIZARD_STEPS: Array<{ slug: Exclude<RfqWizardStep, 'submitted'>; label: string }> = [
  { slug: 'deal', label: 'مشخصات معامله' },
  { slug: 'spec', label: 'مشخصات فنی کالا' },
  { slug: 'weight', label: 'شرایط وزنی' },
  { slug: 'payment', label: 'قیمت و پرداخت' },
  { slug: 'delivery', label: 'تحویل و اعتبار' },
  { slug: 'review', label: 'بازبینی نهایی' },
]

/** Where an old demand wizard link lands: the step that now holds its first field. */
export const RFQ_WIZARD_LEGACY_STEPS: Record<RfqWizardLegacyStep, RfqWizardStep> = {
  product: 'deal',
  terms: 'deal',
}

export const ONBOARDING_PHASES = ['نوع شخص', 'اطلاعات', 'قرارداد'] as const

/** The bullet each screen lights, one based. */
export const ONBOARDING_PHASE_OF: Record<OnboardingStepSlug, number> = {
  account: 1,
  type: 1,
  profile: 2,
  contract: 3,
  done: 3,
  pending: 3,
  rejected: 2,
}

function withQuery(path: string, params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
  if (!entries.length) return path
  const qs = entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&')
  return `${path}?${qs}`
}

export { withQuery }
